import React, {Fragment, useState} from 'react';
import PropTypes from 'prop-types';
import {LockIcon} from 'web-components';
import {UnlockIcon} from 'web-components/icons';
import TableRow from './budget_table_data_row';
import CollapseAll from '../../../../../../images/collapse-all-icon';
import Util from '../../../../../../forecast-app/shared/util/util';
import BudgetLockTooltip from './budget_lock_tooltip';
import ForecastTooltip from '../../../../../../forecast-app/shared/components/tooltips/ForecastTooltip';

const BudgetTable = ({name, data, dataType, showMilestones, showRoles, color, highlightColumnIndex, projectTotals, columnTooltipText, lockedColumnIndexMap, disabledColumnIndexes, withCollapseAll, canExpand, rowTooltipText, currencySymbol}) => {
	const collapsedTables = localStorage.getItem('collapsed_tables_budgetV2')
		? JSON.parse(localStorage.getItem('collapsed_tables_budgetV2')).hasOwnProperty(dataType)
			? JSON.parse(localStorage.getItem('collapsed_tables_budgetV2'))[dataType]
			: true
		: true;

	const [isTableExpanded, setIsTableExpanded] = useState(collapsedTables);

	const handleExpandTable = () => {
		setIsTableExpanded(!isTableExpanded);

		let collapsedTablesInLocal = {};
		if (localStorage.getItem('collapsed_tables_budgetV2')) {
			collapsedTablesInLocal = JSON.parse(localStorage.getItem('collapsed_tables_budgetV2'));
		}
		collapsedTablesInLocal[dataType] = !isTableExpanded;
		Util.localStorageSetItem('collapsed_tables_budgetV2', JSON.stringify(collapsedTablesInLocal));
	};
	const columnWidth = 99 / (data[0] ? data[0].length : 1 - 1);

	return (
		<table className={`budget-table`}>
			<tbody>
				{data.map((row, i) =>
					i === 0 && row !== null ? (
						<tr key={i}>
							{row.map((columnName, j) => {
								if (columnName !== null)
									return (
										<Fragment key={j}>
											<th
												className={`${lockedColumnIndexMap.get(j) && lockedColumnIndexMap.get(j).fullLock && !projectTotals ? ' locked ' : ''}${projectTotals ? 'totals-table' : ''}${
													highlightColumnIndex === j ? ' highlighted' : ''
												}${columnName.length > 1 ? ' two-lines' : ''}${disabledColumnIndexes && disabledColumnIndexes.indexOf(j) > -1 ? ' disabled' : ''}`}
												style={{width: projectTotals ? (j > 0 ? columnWidth : 1) + '%' : null}}
											>
												<div className={` column-name`} title={columnTooltipText && columnTooltipText[j] ? null : columnName}>
													{columnName.map((textLine, k) => (
														<div>
															<div key={k} className={`${k === 1 ? 'second-line' : ''}`}>
																{lockedColumnIndexMap.get(j) && k === 0 && !projectTotals ? (
																	<div className={'locked-icon'}>
																		<ForecastTooltip
																			content={<BudgetLockTooltip lock={lockedColumnIndexMap.get(j)} currencySymbol={currencySymbol} />}
																			disabled={!lockedColumnIndexMap.get(j)}
																			placement={'right'}
																			maxWidth={400}
																		>
																			{lockedColumnIndexMap.get(j).fullLock ? <LockIcon color={'#4C4C58'} size={LockIcon.SIZE.STANDARD} /> : <UnlockIcon color={'#4C4C58'} size={'medium'} />}
																		</ForecastTooltip>
																	</div>
																) : (
																	''
																)}

																<div>
																	{textLine ? (
																		textLine
																	) : canExpand && withCollapseAll ? (
																		<CollapseAll toggleCollapse={() => handleExpandTable()} isCollapsed={!isTableExpanded} userpilot="expand-all-table-button" />
																	) : (
																		''
																	)}
																</div>
															</div>
														</div>
													))}
												</div>
											</th>
										</Fragment>
									);
								return false;
							})}
						</tr>
					) : row !== null ? (
						<TableRow
							showMilestones={showMilestones}
							showRoles={showRoles}
							isTableExpanded={isTableExpanded}
							key={i}
							tableName={name}
							row={row}
							color={color}
							highlightColumnIndex={highlightColumnIndex}
							projectTotals={projectTotals}
							columnWidth={columnWidth}
							lockedColumnIndexMap={lockedColumnIndexMap}
							disabledColumnIndexes={disabledColumnIndexes}
							canExpand={canExpand}
							columnTooltipText={columnTooltipText}
							rowTooltipText={rowTooltipText}
						/>
					) : (
						false
					)
				)}
			</tbody>
		</table>
	);
};

BudgetTable.propTypes = {
	data: PropTypes.array,
	disabledColumnIndexes: PropTypes.arrayOf(PropTypes.number),
	canExpand: PropTypes.bool,
};

export default BudgetTable;

// example of the data prop for a budget table
//[
// [[null], ['week 23', 'feb 23 - nov 16'], ['week 23', 'feb 23 - nov 16'], ['week 23', 'feb 23 - nov 16'], ['total']],
// [['Revenue', 'Total'], [100, 10], [20, 30], [30, 60], [42]],
// [['Cost', 'Total'], [10, 10], [20, 30], [30, 60], [42]],
// [['Profit', 'Total'], [10, 10], [20, 30], [30, 60], [42]]
//]

// example of the data prop for a table with breakdown
// [
// 	[[null], ['July'], ['August'], ['September'], ['Total']],
// 	[
// 		['Revenue', ['No Milestone', ['Project Manager']], ['New Milestone', ['No role', 'Product Owner', 'Project Manager']], ['milestone 28', ['Project Manager']], 'Acc. Totals'],
// 		['$ 750.00', ['$ 0.00', ['$ 0.00']], ['$ 450.00', ['$ 0.00', '$ 0.00', '$ 450.00']], ['$ 300.00', ['$ 300.00']], '$ 750.00'],
// 		['$ 1,420.00', ['$ 450.00', ['$ 450.00']], ['$ 970.00', ['$ 220.00', '$ 750.00', '$ 0.00']], ['$ 0.00', ['$ 0.00']], '$ 2,170.00'],
// 		['$ 0.00', ['$ 0.00', ['$ 0.00']], ['$ 0.00', ['$ 0.00', '$ 0.00', '$ 0.00']], ['$ 0.00', ['$ 0.00']], '$ 2,170.00'],
// 		['$ 2,170.00']
// 	],
// 	[
// 		['Cost', ['No Milestone', ['Project Manager']], ['New Milestone', ['No role', 'Product Owner', 'Project Manager']], ['milestone 28', ['Project Manager']], 'Acc. Totals'],
// 		['$ 100.00', ['$ 0.00', ['$ 0.00']], ['$ 60.00', ['$ 0.00', '$ 0.00', '$ 60.00']], ['$ 40.00', ['$ 40.00']], '$ 100.00'],
// 		['$ 300.00', ['$ 60.00', ['$ 60.00']], ['$ 240.00', ['$ 140.00', '$ 100.00', '$ 0.00']], ['$ 0.00', ['$ 0.00']], '$ 400.00'],
// 		['$ 0.00', ['$ 0.00', ['$ 0.00']], ['$ 0.00', ['$ 0.00', '$ 0.00', '$ 0.00']], ['$ 0.00', ['$ 0.00']], '$ 400.00'],
// 		['$ 400.00']
// 	],
// 	[['Profit', 'Acc. Totals'], ['$ 650.00', '$ 650.00'], ['$ 1,120.00', '$ 1,770.00'], ['$ 0.00', '$ 1,770.00'], ['$ 1,770.00']]
// ];
